import type { FuelLog, Car } from "../model.ts";
import type { Economy } from "./economy.ts";

export interface StationData {
  name: string;
  avgConsumption: number;
  entryCount: number;
  totalDistance: number;
}

export interface StationTotals {
  name: string;
  totalVolume: number;
  totalCost: number;
  entryCount: number;
  avgPricePerUnit: number;
  hasKwh: boolean;
}

export interface GradeConsumption {
  grade: string;
  avgConsumption: number;
  totalDistance: number;
  fillCount: number;
}

export interface SpeedSegment {
  date: number | null;
  speed: number;
  distance: number;
}

function ascending(logs: FuelLog[]) {
  return [...logs].sort((a, b) => (a.odometer ?? 0) - (b.odometer ?? 0));
}

export function stationConsumptions(logs: FuelLog[], economy: Economy, car: Car): StationData[] {
  const ord = ascending(logs);
  if (ord.length < 2) return [];

  interface Raw { consumption: number; rawDistance: number; station: string | null | undefined }
  const raw: Raw[] = [];

  if (car.vehicleType === "phev") {
    ord.filter((l) => l.volumeAdded > 0).forEach((curr, i, a) => {
      if (i === 0) return;
      const prev = a[i - 1]!;
      const d = curr.odometer - prev.odometer;
      if (d <= 0) return;
      const v = economy.consumptionValue(d, curr.volumeAdded, false);
      if (v != null) raw.push({ consumption: v, rawDistance: d, station: curr.stationName });
    });
    ord.filter((l) => (l.kwhAdded ?? 0) > 0).forEach((curr, i, a) => {
      if (i === 0) return;
      const prev = a[i - 1]!;
      const d = curr.odometer - prev.odometer;
      if (d <= 0) return;
      const kwh = curr.kwhAdded ?? 0;
      const v = economy.consumptionValue(d, kwh, true);
      if (v != null) raw.push({ consumption: v, rawDistance: d, station: curr.stationName });
    });
  } else {
    for (let i = 1; i < ord.length; i++) {
      const prev = ord[i - 1]!;
      const curr = ord[i]!;
      const isFuel = curr.volumeAdded > 0;
      const isElec = (curr.kwhAdded ?? 0) > 0;
      if (isFuel === isElec) continue;
      const isElectric = isElec;
      const qty = isElectric ? (curr.kwhAdded ?? 0) : curr.volumeAdded;
      const d = curr.odometer - prev.odometer;
      if (d <= 0) continue;
      const v = economy.consumptionValue(d, qty, isElectric);
      if (v != null) raw.push({ consumption: v, rawDistance: d, station: curr.stationName });
    }
  }

  const entryCounts = new Map<string, number>();
  for (const l of ord) {
    if (l.stationName) entryCounts.set(l.stationName, (entryCounts.get(l.stationName) ?? 0) + 1);
  }

  const groups = new Map<string, Raw[]>();
  for (const r of raw) {
    if (!r.station) continue;
    const arr = groups.get(r.station) ?? [];
    arr.push(r);
    groups.set(r.station, arr);
  }

  return Array.from(groups.entries())
    .filter(([_, segs]) => segs.length >= 2)
    .map(([name, segs]) => ({
      name,
      avgConsumption: segs.reduce((s, r) => s + r.consumption, 0) / segs.length,
      entryCount: entryCounts.get(name) ?? 0,
      totalDistance: economy.convertDistance(segs.reduce((s, r) => s + r.rawDistance, 0)),
    }))
    .sort((a, b) => b.entryCount - a.entryCount);
}

export function stationConsumptionTotals(logs: FuelLog[]): StationTotals[] {
  const groups = new Map<string, { vol: number; cost: number; count: number; kwh: number }>();
  for (const l of logs) {
    if (!l.stationName) continue;
    const g = groups.get(l.stationName) ?? { vol: 0, cost: 0, count: 0, kwh: 0 };
    g.vol += l.volumeAdded;
    g.cost += l.totalCost;
    g.count += 1;
    g.kwh += l.kwhAdded ?? 0;
    groups.set(l.stationName, g);
  }
  return Array.from(groups.entries())
    .map(([name, d]) => {
      const vol = d.vol + d.kwh;
      const avgPrice = vol > 0 ? d.cost / vol : 0;
      return {
        name,
        totalVolume: Math.round(d.vol * 100) / 100,
        totalCost: Math.round(d.cost * 100) / 100,
        entryCount: d.count,
        avgPricePerUnit: Math.round(avgPrice * 1000) / 1000,
        hasKwh: d.kwh > 0,
      };
    })
    .sort((a, b) => b.totalCost - a.totalCost);
}

export function gradeConsumptions(logs: FuelLog[], economy: Economy, car: Car): GradeConsumption[] {
  const ord = ascending(logs);
  if (ord.length < 2) return [];

  const totalDistMap = new Map<string, number>();
  const totalQtyMap = new Map<string, number>();
  const entryCountMap = new Map<string, number>();

  for (let i = 1; i < ord.length; i++) {
    const prev = ord[i - 1]!;
    const curr = ord[i]!;
    const dist = Math.max(curr.odometer - prev.odometer, 0);
    if (dist <= 0) continue;

    const hasFuel = curr.volumeAdded > 0;
    const hasElec = (curr.kwhAdded ?? 0) > 0;

    if (hasFuel && hasElec) {
      const mfgL = car.manufacturerAvgConsumption ?? 5.5;
      const mfgKwh = car.manufacturerAvgEnergy ?? 16.0;
      const literToKwhRatio = mfgKwh / mfgL;
      const fuelEquivalentKwh = curr.volumeAdded * literToKwhRatio;
      const elecKwh = curr.kwhAdded ?? 0;
      const totalEquivalentKwh = fuelEquivalentKwh + elecKwh;

      if (totalEquivalentKwh > 0) {
        const dFuel = dist * (fuelEquivalentKwh / totalEquivalentKwh);
        const dElec = dist * (elecKwh / totalEquivalentKwh);
        const fGrade = curr.fuelGrade ?? "Regular 95";

        totalDistMap.set(fGrade, (totalDistMap.get(fGrade) ?? 0) + dFuel);
        totalQtyMap.set(fGrade, (totalQtyMap.get(fGrade) ?? 0) + curr.volumeAdded);
        entryCountMap.set(fGrade, (entryCountMap.get(fGrade) ?? 0) + 1);

        totalDistMap.set("Electric", (totalDistMap.get("Electric") ?? 0) + dElec);
        totalQtyMap.set("Electric", (totalQtyMap.get("Electric") ?? 0) + elecKwh);
        entryCountMap.set("Electric", (entryCountMap.get("Electric") ?? 0) + 1);
      }
    } else if (hasFuel) {
      const g = curr.fuelGrade ?? "Regular 95";
      totalDistMap.set(g, (totalDistMap.get(g) ?? 0) + dist);
      totalQtyMap.set(g, (totalQtyMap.get(g) ?? 0) + curr.volumeAdded);
      entryCountMap.set(g, (entryCountMap.get(g) ?? 0) + 1);
    } else if (hasElec) {
      totalDistMap.set("Electric", (totalDistMap.get("Electric") ?? 0) + dist);
      totalQtyMap.set("Electric", (totalQtyMap.get("Electric") ?? 0) + (curr.kwhAdded ?? 0));
      entryCountMap.set("Electric", (entryCountMap.get("Electric") ?? 0) + 1);
    }
  }

  return Array.from(totalDistMap.entries())
    .map(([grade, dKm]) => {
      if (dKm <= 0) return null;
      const qty = totalQtyMap.get(grade);
      if (qty == null || qty <= 0) return null;
      const isElectric = grade === "Electric";
      const avg = economy.consumptionValue(dKm, qty, isElectric);
      if (avg == null) return null;
      return { grade, avgConsumption: avg, totalDistance: economy.convertDistance(dKm), fillCount: entryCountMap.get(grade) ?? 0 };
    })
    .filter((g): g is GradeConsumption => g != null)
    .sort((a, b) => b.totalDistance - a.totalDistance);
}

export function avgSpeedSegments(logs: FuelLog[], economy: Economy): SpeedSegment[] {
  const ord = ascending(logs);
  if (ord.length < 2) return [];
  const out: SpeedSegment[] = [];
  for (let i = 1; i < ord.length; i++) {
    const s = ord[i]?.avgSpeed;
    if (s == null || s <= 0) continue;
    const d = ord[i]!.odometer - ord[i - 1]!.odometer;
    if (d > 0) out.push({ date: ord[i]!.date ?? null, speed: s, distance: economy.convertDistance(d) });
  }
  return out.slice(-12);
}
